import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../components/Header';
import LeftSidebar from '../components/LeftSidebar';
import MainContent from '../components/MainContent';
import CaseDetailModal from '../components/CaseDetailModal';
import CompanyDetailModal from '../components/CompanyDetailModal';
import ContribuieModal from '../components/ContribuieModal';
import Footer from '../components/Footer';
import { HomeHero } from '../components/HomeHero';
import SEOHead from '../components/SEOHead';
import { search as apiSearch, searchByDosar } from '../lib/api';
import { trackSearch, trackDosarSearch, trackViewCase } from '../services/analytics';
import type { Filters, SelectedFilters } from '../types';
import { normalizeSearchResults, buildDynamicFilters, isObiectMatching, removeDiacritics } from '@/lib/dynamicFilterHelpers';
import { useAuth } from '../context/AuthContext';

interface SearchPageProps {
    initialSituatie?: string;
    initialMaterie?: string;
    pageTitle?: string;
}

const SearchPage: React.FC<SearchPageProps> = ({
    initialSituatie = '',
    initialMaterie = '',
    pageTitle
}) => {
    const { user } = useAuth();
    // const [filters, setFilters] = useState<Filters | null>(null); // Removed static filters state
    const [dynamicFilters, setDynamicFilters] = useState<Filters | null>(null);
    const [originalResults, setOriginalResults] = useState<any[]>([]); // Store all fetched results for client-side filtering
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [status, setStatus] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [situatie, setSituatie] = useState(initialSituatie);
    const [searchParams, setSearchParams] = useState<SelectedFilters>({
        materie: initialMaterie || '',
        obiect: [],
        tip_speta: [],
        parte: [],
    } as SelectedFilters);
    const [selectedCase, setSelectedCase] = useState<any | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState<any | null>(null);
    const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
    const [isContribuieModalOpen, setIsContribuieModalOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isProEnabled] = useState(true);
    const [acteJuridice, setActeJuridice] = useState<string[]>([]);
    const [isDosarSearchLoading, setIsDosarSearchLoading] = useState(false);

    const [dosarSearchInfo, setDosarSearchInfo] = useState<{ obiect: string; numar: string; materie?: string | null } | null>(null);
    const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
    // Track if a search has been initiated to determine Home vs Results view
    const [hasSearched, setHasSearched] = useState(false);

    // Apply client-side filters whenever searchParams or originalResults change
    const applyClientSideFilters = useCallback(() => {
        if (!originalResults.length) {
            setSearchResults([]);
            return;
        }

        let filtered = [...originalResults];

        // 1. Filter by Materie
        if (searchParams.materie) {
            filtered = filtered.filter(r => {
                const rData = r.data || r;
                // Robust comparison: normalize strings to handle diacritics or casing differences
                if (!rData.materie) return false;
                return removeDiacritics(rData.materie).toLowerCase() === removeDiacritics(searchParams.materie).toLowerCase();
            });
        }

        // 2. Filter by Obiect (OR logic within obiects)
        if (searchParams.obiect.length > 0) {
            filtered = filtered.filter(r => {
                const rData = r.data || r;
                const rObiectRaw = rData.obiectul || rData.obiect || '';
                const rParts = rObiectRaw.split(/,|;|\s+și\s+/i).map((s: string) => s.trim()).filter(Boolean);

                // Use fuzzy matching for filtering too
                // Check if ANY selected filter matches ANY part of the item's object
                return searchParams.obiect.some(selected =>
                    rParts.some((part: string) => isObiectMatching(selected, part))
                );
            });
        }

        // 3. Filter by Tip Speta
        if (searchParams.tip_speta.length > 0) {
            filtered = filtered.filter(r => {
                const rData = r.data || r;
                const rTip = rData.tip_speta || rData.tip || rData.categorie_speta || '';
                return searchParams.tip_speta.some(selected =>
                    removeDiacritics(selected).toLowerCase() === removeDiacritics(rTip).toLowerCase()
                );
            });
        }

        // 4. Filter by Parte
        if (searchParams.parte.length > 0) {
            filtered = filtered.filter(r => {
                const rData = r.data || r;
                const rParte = rData.parte || rData.parti || '';
                const parts = rParte.split(/,|;/).map((s: string) => s.trim());

                return searchParams.parte.some(p =>
                    parts.some((part: string) => removeDiacritics(part).toLowerCase() === removeDiacritics(p).toLowerCase())
                );
            });
        }

        setSearchResults(filtered);

        const newFilters = buildDynamicFilters(originalResults, {
            materie: searchParams.materie,
            obiect: searchParams.obiect,
            tip_speta: searchParams.tip_speta,
            parte: searchParams.parte
        });
        setDynamicFilters(newFilters);

    }, [originalResults, searchParams]);


    // Trigger filter application when deps change
    useEffect(() => {
        applyClientSideFilters();
    }, [applyClientSideFilters]);

    // Auto-search if initialSituatie provided
    useEffect(() => {
        if (initialSituatie) {
            handleSearch();
        }
    }, []); // Run once on mount if prop exists

    // Handle imported results (e.g. from Relevant Cases in Legislation)
    const location = useLocation();
    useEffect(() => {
        // @ts-ignore
        if (location.state?.importedResults) {
            // @ts-ignore
            const imported = location.state.importedResults;
            // @ts-ignore
            const title = location.state.importedContext || 'Rezultate Importate';

            setHasSearched(true);
            const normalizedImported = normalizeSearchResults(imported);
            setOriginalResults(normalizedImported);
            setSearchResults(normalizedImported);
            setStatus(title + ` (${imported.length} rezultate)`);
            setOffset(imported.length);
            setHasMore(false);

            // Clear history state to prevent reload loops
            window.history.replaceState({}, document.title);
        }
    }, [location]);

    // Example fill logic
    const EXAMPLE_CASE = "Contestatorii au formulat contestație la executare silită împotriva actelor de executare pornite de un executor judecătoresc la cererea creditorului, bazate pe două contracte de împrumut. Aceștia au solicitat anularea actelor de executare, reducerea cheltuielilor de executare și anularea titlurilor executorii (contractele de împrumut), argumentând că prețurile din contracte erau neserioase, sumele împrumutate nu au fost primite integral și că actele ascundeau o operațiune de cămătărie.";

    const handleExampleFill = useCallback(() => {
        setHasSearched(true); // Switch to results/input view technically, but usually stays on Hero until search

        const text = EXAMPLE_CASE;
        let index = 0;
        setSituatie('');

        const typingInterval = setInterval(() => {
            if (index < text.length) {
                setSituatie(() => text.substring(0, index + 1));
                index++;
            } else {
                clearInterval(typingInterval);
            }
        }, 10);
        return () => clearInterval(typingInterval);
    }, []);

    const loadMoreResults = useCallback(async (currentOffset: number) => {
        if (isLoading || !hasMore) return;

        setIsLoading(true);
        setStatus('Se încarcă mai multe rezultate...');

        const payload = {
            ...searchParams,
            situatie,
            materie: searchParams.materie ? [searchParams.materie] : [],
            offset: currentOffset,
        };

        try {
            const resultsRaw = await apiSearch(payload);
            const results = normalizeSearchResults(resultsRaw);
            setOriginalResults(prev => [...prev, ...results]);
            setOffset(currentOffset + results.length);
            setHasMore(results.length === 20);
            setStatus(`Au fost găsite ${originalResults.length + results.length} rezultate.`);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Eroare necunoscută';
            setStatus(`Eroare la încărcarea rezultatelor: ${errorMessage}`);
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, hasMore, searchParams, situatie, originalResults.length]);

    const handleSearch = useCallback(async () => {
        setHasSearched(true);
        if (!situatie.trim() && searchParams.obiect.length === 0) {
            setStatus("Introduceți un text sau selectați un obiect pentru a căuta.");
            return;
        }

        setSearchResults([]);
        setOffset(0);
        setHasMore(true);
        setIsLoading(true);
        setIsLoading(true);
        setStatus(''); // Clear previous status immediately

        // Track the search event
        if (situatie.trim()) {
            trackSearch(situatie);
        }


        try {
            const payload = {
                ...searchParams,
                situatie,
                materie: searchParams.materie ? [searchParams.materie] : [],
                offset: 0,
            };

            const initialResultsRaw = await apiSearch(payload);
            const initialResults = normalizeSearchResults(initialResultsRaw);

            setOriginalResults(initialResults);
            const newFilters = buildDynamicFilters(initialResults, {
                materie: searchParams.materie,
                obiect: searchParams.obiect,
                tip_speta: searchParams.tip_speta,
                parte: searchParams.parte
            });
            setDynamicFilters(newFilters);

            if (isProEnabled && situatie.trim().split(/\s+/).length > 3) {
                // AI Analysis Logic
                try {
                    setStatus("Analizez contextul juridic cu AI pentru a găsi cea mai relevantă spetă... (poate dura câteva minute)");
                    const response = await fetch('/api/settings/analyze-llm-data', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                    });

                    if (!response.ok) throw new Error('Failed to start AI analysis');
                    const startResponse = await response.json();
                    if (!startResponse.success || !startResponse.job_id) throw new Error(startResponse.message || 'Failed to start AI analysis');

                    const jobId = startResponse.job_id;
                    const pollInterval = 2000;
                    const maxPolls = 600;
                    let pollCount = 0;

                    const pollStatus = async (): Promise<any> => {
                        const statusResponse = await fetch(`/api/settings/analyze-llm-status/${jobId}`);
                        if (!statusResponse.ok) throw new Error('Failed to check status');
                        return await statusResponse.json();
                    };

                    while (pollCount < maxPolls) {
                        const statusData = await pollStatus();
                        if (statusData.status === 'completed') {
                            if (statusData.result && statusData.result.success) {
                                const aiSelectedIds = statusData.result.ai_selected_ids || [];
                                const allCandidates = statusData.result.all_candidates || [];
                                const extractedActe = statusData.result.acte_juridice || [];
                                setActeJuridice(extractedActe);

                                if (aiSelectedIds.length > 0) {
                                    // Process logic similar to before
                                    const aiSelectedResults = aiSelectedIds.map((id: number) => {
                                        const candidate = allCandidates.find((c: any) => c.id === id);
                                        if (candidate) {
                                            return {
                                                id: candidate.id,
                                                data: candidate,
                                                score: 100,
                                                situatia_de_fapt_full: candidate.situatia_de_fapt,
                                                argumente_instanta: candidate.argumente_instanta || 'Nu există date',
                                                text_individualizare: candidate.text_individualizare || 'Nu există date',
                                                text_doctrina: candidate.text_doctrina || 'Nu există date',
                                                text_ce_invatam: candidate.text_ce_invatam || 'Nu există date',
                                                Rezumat_generat_de_AI_Cod: candidate.Rezumat_generat_de_AI_Cod || 'Nu există date',
                                                sugestie_llm_taxa: candidate.sugestie_llm_taxa // Preserve tax suggestion
                                            };
                                        }
                                        return initialResults.find((item: any) => item.id === id);
                                    }).filter(Boolean);

                                    const candidateIds = allCandidates.map((c: any) => c.id);
                                    const remainingCandidates = initialResults.filter((item: any) =>
                                        candidateIds.includes(item.id) && !aiSelectedIds.includes(item.id)
                                    ).sort((a: any, b: any) => (b.score || 0) - (a.score || 0));

                                    const exclusiveDisplay = statusData.result.exclusive_display || false;
                                    let mergedResults;

                                    if (exclusiveDisplay) {
                                        mergedResults = aiSelectedResults.map((r: any) => ({ ...r, isAISelected: true }));
                                        // Status update removed as per user request
                                    } else {
                                        mergedResults = [
                                            ...aiSelectedResults.map((r: any) => ({ ...r, isAISelected: true })),
                                            ...remainingCandidates.map((r: any) => ({ ...r, isCandidateCase: true }))
                                        ];
                                        // Status update removed as per user request
                                    }
                                    setOriginalResults(mergedResults);
                                    setOffset(mergedResults.length);
                                    setHasMore(false);
                                } else {
                                    setOriginalResults([]);
                                    setStatus("AI-ul nu a găsit rezultate relevante.");
                                }
                            } else {
                                setOriginalResults([]);
                                setSearchResults([]);
                                setStatus("Eroare la procesarea răspunsului AI.");
                            }
                            break;
                        } else if (statusData.status === 'failed') {
                            throw new Error(statusData.error || 'AI analysis failed');
                        } else if (statusData.status === 'queued' || statusData.status === 'processing') {
                            setStatus(statusData.status === 'queued'
                                ? `În coadă pentru analiză AI... `
                                : "AI analizează datele...");
                        } else if (statusData.status === 'not_found') {
                            throw new Error('Job not found');
                        }
                        await new Promise(resolve => setTimeout(resolve, pollInterval));
                        pollCount++;
                    }
                    if (pollCount >= maxPolls) throw new Error('AI analysis timeout');
                } catch (aiError) {
                    console.error("AI Filtering failed:", aiError);
                    setStatus("Filtrarea AI nu a putut fi aplicată.");
                    setOriginalResults([]);
                    setSearchResults([]);
                }
            } else {
                setOffset(initialResults.length);
                setHasMore(initialResults.length === 20); // This logic might be slightly off with limits but keeps paging working

                // Normalize role
                let userRole = 'guest';
                if (user) {
                    userRole = (user.rol || 'basic').toLowerCase();
                }

                // Determine strict role limit
                let limit = 10;
                if (userRole === 'admin') limit = 100;
                else if (userRole === 'pro') limit = 50;
                else if (userRole === 'basic') limit = 20;
                else limit = 10; // guest

                // Check if we hit the limit
                // If we received exactly the limit, we assume we hit it.
                // Or if we have accumulated >= limit
                const totalLoaded = initialResults.length;
                let limitHit = totalLoaded >= limit;

                // Stop pagination if we hit role limit
                if (limitHit && userRole !== 'admin') {
                    setHasMore(false);
                } else {
                    setHasMore(initialResults.length === 20); // Standard pagination check
                }

                // Set offset correctly
                setOffset(initialResults.length);

                let statusMsg = `Au fost găsite ${initialResults.length} rezultate.`;

                // Only show upgrade message for guest and basic users if limit is reached
                if (limitHit && (userRole === 'guest' || userRole === 'basic')) {
                    statusMsg += ` (Limitat la ${limit} pentru ${userRole || 'neînregistrat'}. Upgrade pentru mai multe.)`;
                }
                setStatus(statusMsg);
            }

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Eroare necunoscută';
            setStatus(`Eroare la încărcarea rezultatelor: ${errorMessage}`);
        } finally {
            setIsLoading(false);
        }
    }, [situatie, searchParams, isProEnabled]);

    const handleDosarSearch = useCallback(async (numarDosar: string) => {
        setHasSearched(true);
        setIsDosarSearchLoading(true);
        setStatus('Căutare după număr dosar...');

        trackDosarSearch(numarDosar);

        try {
            const response = await searchByDosar(numarDosar);
            if (!response.success) {
                setStatus(`Eroare: ${response.error || 'Căutarea a eșuat'}`);
                setSearchResults([]);
                setDosarSearchInfo(null);
                return;
            }

            setDosarSearchInfo({
                obiect: response.obiect_from_portal || '',
                materie: response.materie_from_portal,
                numar: response.numar_dosar
            });

            const normalizedResults = normalizeSearchResults(response.results);
            setOriginalResults(normalizedResults);
            setOffset(normalizedResults.length);
            setHasMore(false);
            setActeJuridice([]);

            if (response.results.length > 0) {
                setStatus(`Căutare după număr dosar "${numarDosar}": ${response.match_count} spețe similare găsite.`);
            } else {
                setStatus(`Nu au fost găsite spețe similare pentru dosarul "${numarDosar}"`);
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Eroare necunoscută';
            setStatus(`Eroare la căutarea după număr dosar: ${errorMessage}`);
            setSearchResults([]);
            setDosarSearchInfo(null);
        } finally {
            setIsDosarSearchLoading(false);
        }
    }, []);

    const handleFilterChange = useCallback((filterType: keyof SelectedFilters, value: any) => {
        setSearchParams(prevParams => {
            const newParams = { ...prevParams, [filterType]: value };
            if (filterType === 'materie') {
                newParams.obiect = [];
            }
            return newParams;
        });
    }, []);

    const handleViewCase = (caseData: any) => {
        if (caseData.type === 'company') {
            setSelectedCompany(caseData);
            setIsCompanyModalOpen(true);
        } else {

            setSelectedCase(caseData);
            setIsModalOpen(true);
            // Track view case
            if (caseData && (caseData.titlu || caseData.id)) {
                trackViewCase(caseData.titlu || `Case ID: ${caseData.id}`);
            }
        }
    };

    const handleRemoveFilter = useCallback((filterType: string, valueToRemove: string) => {
        setSearchParams(prevParams => {
            const newParams = { ...prevParams };
            const key = filterType as keyof SelectedFilters;
            const currentValue = newParams[key];
            if (Array.isArray(currentValue)) {
                // @ts-ignore
                newParams[key] = currentValue.filter(item => item !== valueToRemove);
            } else {
                // @ts-ignore
                newParams[key] = '';
            }
            return newParams;
        });
    }, []);

    const handleClearFilters = useCallback(() => {
        setSearchParams({
            materie: '',
            obiect: [],
            tip_speta: [],
            parte: [],
        });
    }, []);

    const handleReset = useCallback(() => {
        setHasSearched(false);
        setSearchResults([]);
        setOriginalResults([]);
        setStatus('');
        setSituatie('');
        setSearchParams({
            materie: '',
            obiect: [],
            tip_speta: [],
            parte: [],
        });
        setDosarSearchInfo(null);
        setActeJuridice([]);
        setIsLoading(false);
        setOffset(0);
        setHasMore(true);
        // Ensure reset happens even if already on home but with leftover state
    }, []);

    // Determine view state
    const isHomeView = !hasSearched && originalResults.length === 0 && !isLoading && !dosarSearchInfo && status === '';

    // Structured data for SEO
    const searchPageStructuredData = [
        {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": "https://chat.legeaaplicata.ro/#webpage",
            "url": "https://chat.legeaaplicata.ro/",
            "name": "Analiză Juridică AI - Cercetare Jurisprudență",
            "description": "Platformă de cercetare juridică cu AI pentru căutare avansată în jurisprudență română, hotărâri ICCJ, CEDO și legislație actualizată",
            "isPartOf": {
                "@id": "https://chat.legeaaplicata.ro/#website"
            },
            "about": {
                "@type": "Thing",
                "name": "Cercetare Juridică"
            },
            "primaryImageOfPage": {
                "@type": "ImageObject",
                "url": "https://chat.legeaaplicata.ro/src/assets/icons/logo.webp"
            }
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Acasă",
                    "item": "https://chat.legeaaplicata.ro/"
                }
            ]
        }
    ];

    return (
        <div className="flex flex-col min-h-screen bg-brand-light">
            <SEOHead
                title={pageTitle || "Analiză Juridică AI - Cercetare Jurisprudență România"}
                description="Căutare avansată în jurisprudență română cu inteligență artificială. Accesați hotărâri ICCJ, CEDO, legislație actualizată, analiză comparativă spețe, monitoring dosare și verificare bonitate. Versiune offline 100% confidențială pentru avocați și jurisconsulți."
                keywords="juridic ai, ai juridic, cercetare juridică AI, jurisprudență România, hotărâri ICCJ, hotărâri CEDO, analiză comparativă spețe, monitoring dosare instanță, legislație actualizată, cod civil, cod penal, avocat online, jurisconsult, practică judiciară, decizie instanță, ai juridice, juridice ai abonament, juridice ai student, chat juridic, juridice cont, juridice ai advanced, blog juridic, servicii juridice, consultanta juridica online, asistent juridic virtual, jurisprudenta explicata, legislatie actualizata ai, portal juridic romania, avocat ai, spete comentate"
                canonicalUrl="https://chat.legeaaplicata.ro/"
                structuredData={searchPageStructuredData}
            />
            <Header
                onToggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                onContribuieClick={() => setIsContribuieModalOpen(true)}
                isHomeView={isHomeView}
                onReset={handleReset}
            />

            <div className="flex flex-1 overflow-hidden relative">
                <LeftSidebar
                    filters={dynamicFilters}
                    selectedFilters={searchParams}
                    onFilterChange={handleFilterChange}
                    isOpen={isMobileMenuOpen}
                    onClose={() => setIsMobileMenuOpen(false)}
                    onContribuieClick={() => setIsContribuieModalOpen(true)}
                    isDesktopOpen={isDesktopSidebarOpen}
                    onDesktopToggle={() => setIsDesktopSidebarOpen(!isDesktopSidebarOpen)}
                    hideOnDesktop={isHomeView}
                />

                {isHomeView ? (
                    <HomeHero
                        situacie={situatie}
                        onSituatieChange={setSituatie}
                        onSearch={handleSearch}
                        onDosarSearch={handleDosarSearch}
                        isLoading={isLoading}
                        onExampleClick={handleExampleFill}
                    />
                ) : (
                    <MainContent
                        results={searchResults}
                        status={status}
                        isLoading={isLoading}
                        onViewCase={handleViewCase}
                        searchParams={searchParams}
                        onRemoveFilter={handleRemoveFilter}
                        onClearFilters={handleClearFilters}
                        onLoadMore={() => loadMoreResults(offset)}
                        hasMore={hasMore}
                        situatie={situatie}
                        onSituatieChange={setSituatie}
                        onSearch={handleSearch}

                        acteJuridice={acteJuridice}
                        onDosarSearch={handleDosarSearch}
                        isDosarSearchLoading={isDosarSearchLoading}
                        dosarSearchInfo={dosarSearchInfo}
                    />
                )}
            </div>

            <CaseDetailModal
                isOpen={isModalOpen}
                result={selectedCase}
                onClose={() => setIsModalOpen(false)}
                userQuery={situatie}
            />

            <CompanyDetailModal
                isOpen={isCompanyModalOpen}
                company={selectedCompany}
                onClose={() => setIsCompanyModalOpen(false)}
            />

            <ContribuieModal
                isOpen={isContribuieModalOpen}
                onClose={() => setIsContribuieModalOpen(false)}
            />
            <Footer />
        </div>
    );
};

export default SearchPage;
